import type { ApiComponents } from '@aztec-realms/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { browserApiClient, requiredApiData } from './browser-api';

export type InboxItem = ApiComponents['schemas']['InboxItem'];
export const inboxQueryKey = ['earned-inbox'] as const;
export const ownerInboxKey = (scope: string) => [...inboxQueryKey, scope] as const;

export async function loadInbox(cursor: string | undefined, signal?: AbortSignal) {
  const response = await browserApiClient().GET('/api/v1/notifications/inbox', {
    params: { query: { limit: 20, cursor } },
    signal,
  });
  return requiredApiData(response.data, response.error, 'Your inbox could not be loaded.');
}

export async function loadInboxItem(id: string, signal?: AbortSignal) {
  const response = await browserApiClient().GET('/api/v1/notifications/inbox/{item_id}', {
    params: { path: { item_id: id } },
    signal,
  });
  return requiredApiData(response.data, response.error, 'This thank-you is unavailable.');
}

export async function markInboxRead(id: string) {
  if (!navigator.onLine) throw new Error('Reconnect to mark this thank-you as read.');
  const response = await browserApiClient({ mutation: true }).POST(
    '/api/v1/notifications/inbox/{item_id}/read',
    { params: { path: { item_id: id } } },
  );
  return requiredApiData(response.data, response.error, 'Read status could not be saved.');
}

export function useEarnedInbox(scope: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...ownerInboxKey(scope), 'list'],
    queryFn: ({ pageParam, signal }) => loadInbox(pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
  });
}

export function useEarnedInboxItem(scope: string, id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...ownerInboxKey(scope), 'item', id],
    queryFn: ({ signal }) => loadInboxItem(id!, signal),
    enabled: enabled && Boolean(id),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useReadInboxItem(scope: string) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: [...ownerInboxKey(scope), 'read'],
    mutationFn: markInboxRead,
    networkMode: 'always',
    retry: false,
    gcTime: 0,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ownerInboxKey(scope) });
    },
  });
}

const receiptIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function inboxReceiptId(pathname: string, search: string): string | undefined {
  if (pathname !== '/tribe') return undefined;
  const id = new URLSearchParams(search).get('inbox');
  return id && receiptIdPattern.test(id) ? id : undefined;
}

/** A receipt can only navigate to its own allowlisted, same-origin read surface. */
export function inboxActionPath(item: InboxItem): string | undefined {
  const expected = `/tribe?inbox=${item.id}`;
  return item.context_available && receiptIdPattern.test(item.id) && item.action_path === expected
    ? expected
    : undefined;
}
